import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { getOrCreateDirectMessage, fetchDirectMessages } from '../../store/slices/channelSlice';
import { addNotification } from '../../store/slices/uiSlice';
import { setCurrentChannel } from '../../store/slices/chatSlice';

import { API_URL } from '../../config/api';
import PresignedAvatar from './PresignedAvatar';
import SharedFilesList from '../chat/SharedFilesList';
import { fetchUsers } from '../../store/slices/userSlice';
import { getRealmDoc, realmAvailable } from '../../realm';
import { findDirectMessageForPeer } from '../../utils/directMessageUtils';
import { formatPresenceLabel, presenceDotClass, resolveWorkLocation } from '../../utils/presenceLabelUtils';
import MemberRelationshipEditor from './MemberRelationshipEditor';
import {
  deleteRelationshipWith,
  fetchRelationshipWith,
  saveRelationshipWith,
} from '../../services/memberRelationshipService';

const SOCIAL_LINK_KEYS = ['whatsapp', 'facebook', 'instagram', 'linkedin', 'github', 'zoom', 'googleMeet', 'website'];

const hasAnySocialLink = (socialLinks) => {
  if (!socialLinks) return false;
  if (SOCIAL_LINK_KEYS.some((key) => socialLinks[key]?.trim?.())) return true;
  return (socialLinks.customLinks || []).some((link) => link?.title?.trim() && link?.url?.trim());
};

const normalizeExternalUrl = (url) => {
  if (!url) return '';
  const trimmed = url.trim();
  return trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;
};

const normalizePhoneForTel = (phone) => phone?.replace(/[^\d+]/g, '') || '';

const buildWhatsappUrl = (value) => (
  value.startsWith('http') ? value : `https://wa.me/${value.replace(/\+/g, '').replace(/\s/g, '')}`
);

const SOCIAL_PLATFORM_CONFIG = [
  { key: 'whatsapp', label: 'WhatsApp', bgClass: 'bg-[#25D366] hover:bg-[#20bd5a]', buildHref: (v) => buildWhatsappUrl(v.trim()) },
  { key: 'facebook', label: 'Facebook', bgClass: 'bg-[#1877F2] hover:bg-[#166fe0]', buildHref: (v) => normalizeExternalUrl(v) },
  { key: 'instagram', label: 'Instagram', bgClass: 'bg-gradient-to-br from-[#f58529] via-[#dd2a7b] to-[#8134af] hover:opacity-90', buildHref: (v) => normalizeExternalUrl(v) },
  { key: 'linkedin', label: 'LinkedIn', bgClass: 'bg-[#0A66C2] hover:bg-[#0958aa]', buildHref: (v) => normalizeExternalUrl(v) },
  { key: 'github', label: 'GitHub', bgClass: 'bg-[#24292f] hover:bg-[#1b1f23] dark:bg-[#30363d] dark:hover:bg-[#3d444d]', buildHref: (v) => normalizeExternalUrl(v) },
  { key: 'zoom', label: 'Zoom', bgClass: 'bg-[#2D8CFF] hover:bg-[#2480ef]', buildHref: (v) => normalizeExternalUrl(v) },
  { key: 'googleMeet', label: 'Google Meet', bgClass: 'bg-[#00897B] hover:bg-[#00796b]', buildHref: (v) => normalizeExternalUrl(v) },
  { key: 'website', label: 'Website', bgClass: 'bg-indigo-500 hover:bg-indigo-600', buildHref: (v) => normalizeExternalUrl(v) },
];

const buildSocialLinkItems = (socialLinks) => {
  if (!socialLinks) return [];

  const items = SOCIAL_PLATFORM_CONFIG
    .filter(({ key }) => socialLinks[key]?.trim?.())
    .map(({ key, label, buildHref }) => ({
      id: key,
      label,
      href: buildHref(socialLinks[key]),
      platform: key,
    }));

  (socialLinks.customLinks || []).forEach((link, index) => {
    if (link?.title?.trim() && link?.url?.trim()) {
      items.push({
        id: `custom-${index}`,
        label: link.title.trim(),
        href: normalizeExternalUrl(link.url),
        platform: 'custom',
      });
    }
  });

  return items;
};

const SocialPlatformGlyph = ({ platform, className = 'w-5 h-5' }) => {
  switch (platform) {
    case 'whatsapp':
      return (
        <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
        </svg>
      );
    case 'facebook':
      return (
        <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      );
    case 'instagram':
      return (
        <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.981 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
        </svg>
      );
    case 'linkedin':
      return (
        <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
        </svg>
      );
    case 'github':
      return (
        <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
        </svg>
      );
    case 'zoom':
      return (
        <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4.585 17.414A2 2 0 014 16V6.999A2 2 0 016 5h9a2 2 0 011.414.585l3 3A2 2 0 0120 9.414V16a2 2 0 01-2 2H6a2 2 0 01-1.415-.586zM8 8.5v7h8v-7H8z" />
        </svg>
      );
    case 'googleMeet':
      return (
        <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 5a2 2 0 00-2 2v10a2 2 0 002 2h7.5l3.5 3V5H6zm11.2 1.8l3.3 2.4a1 1 0 010 1.6l-3.3 2.4V6.8z" />
        </svg>
      );
    case 'website':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
        </svg>
      );
    default:
      return null;
  }
};

const SocialIconLink = ({ href, label, platform }) => {
  const platformStyle = SOCIAL_PLATFORM_CONFIG.find((item) => item.key === platform);
  const hasGlyph = platform !== 'custom' && SOCIAL_PLATFORM_CONFIG.some((item) => item.key === platform);
  const bgClass = platformStyle?.bgClass || 'bg-gray-500 hover:bg-gray-600';

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={label}
      aria-label={label}
      className={`inline-flex w-10 h-10 rounded-full items-center justify-center text-white shadow-sm transition-all hover:scale-105 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${bgClass}`}
    >
      {hasGlyph ? (
        <SocialPlatformGlyph platform={platform} className="w-5 h-5" />
      ) : (
        <span className="text-sm font-bold leading-none">
          {(label || '?').charAt(0).toUpperCase()}
        </span>
      )}
    </a>
  );
};

const UserProfileModal = ({ userId, isOpen, onClose, onStartConversation }) => {
  const dispatch = useDispatch();
  const { user: currentUser } = useSelector((state) => state.auth);
  const reduxUsers = useSelector((state) => state.users.users || []);
  const directMessages = useSelector((state) => state.channels.directMessages || []);
  const [startingConversation, setStartingConversation] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'files'
  const [dmChannelId, setDmChannelId] = useState(null);
  const [relationship, setRelationship] = useState(null);
  const [relationshipLoading, setRelationshipLoading] = useState(false);
  const [relationshipSaving, setRelationshipSaving] = useState(false);

  useEffect(() => {
    if (isOpen && userId) {
      fetchUserProfile();
      fetchDmChannel();
    } else {
      setUser(null);
      setError(null);
      setActiveTab('profile');
      setDmChannelId(null);
      setRelationship(null);
    }
  }, [isOpen, userId]);

  const loadRelationship = async () => {
    if (!userId || userId === currentUser?.id) {
      setRelationship(null);
      setRelationshipLoading(false);
      return;
    }
    setRelationshipLoading(true);
    try {
      const saved = await fetchRelationshipWith(userId);
      setRelationship(saved || null);
    } catch (err) {
      console.error('Failed to load relationship:', err);
      setRelationship(null);
    } finally {
      setRelationshipLoading(false);
    }
  };

  const handleSaveRelationship = async (body) => {
    setRelationshipSaving(true);
    try {
      const saved = await saveRelationshipWith(userId, body);
      setRelationship(saved);
      dispatch(addNotification({
        type: 'success',
        title: 'Relationship saved',
        message: saved?.explicitRelationLabel
          ? `${saved.explicitRelationLabel} · ${saved.categoryLabel}`
          : 'Relationship updated',
      }));
    } finally {
      setRelationshipSaving(false);
    }
  };

  const handleDeleteRelationship = async () => {
    setRelationshipSaving(true);
    try {
      await deleteRelationshipWith(userId);
      setRelationship(null);
      dispatch(addNotification({
        type: 'success',
        title: 'Relationship removed',
        message: 'This member is no longer marked with a relationship',
      }));
    } finally {
      setRelationshipSaving(false);
    }
  };

  const fetchDmChannel = async () => {
    if (!userId || userId === currentUser?.id) return;
    const existing = findDirectMessageForPeer(directMessages, currentUser?.id, userId);
    if (existing?.id) {
      setDmChannelId(existing.id);
      return;
    }
    try {
      const channel = await dispatch(getOrCreateDirectMessage(userId)).unwrap();
      setDmChannelId(channel.id);
    } catch (err) {
      console.error('Failed to get DM channel:', err);
    }
  };

  const fetchUserProfile = async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const fromRedux = reduxUsers.find((u) => u.id === userId);
      if (fromRedux) {
        setUser(fromRedux);
        loadRelationship();
        return;
      }

      const fromRealm = await getRealmDoc('UserProfile', userId);
      if (fromRealm) {
        setUser(fromRealm);
        loadRelationship();
        return;
      }

      if (await realmAvailable()) {
        dispatch(fetchUsers({ force: true }));
        setError('User not found');
        return;
      }

      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      try {
        const profileResponse = await axios.get(`${API_URL}/users/${userId}`, { headers });
        if (profileResponse.data) {
          setUser(profileResponse.data);
          loadRelationship();
          return;
        }
      } catch (profileErr) {
        console.warn('Failed to fetch user by id, falling back to list lookup:', profileErr);
      }

      const response = await axios.get(`${API_URL}/users`, { headers });
      const foundUser = response.data.find(u => u.id === userId);
      
      if (foundUser) {
        setUser(foundUser);
        loadRelationship();
      } else {
        setError('User not found');
      }
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
      setError('Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isOwnProfile = userId === currentUser?.id;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const formatJoinDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (profileUser) => presenceDotClass(profileUser);

  const getStatusText = (profileUser) => formatPresenceLabel(profileUser);

  const handleStartConversation = async () => {
    if (!userId || userId === currentUser?.id) return;
    
    setStartingConversation(true);
    try {
      const channel = await dispatch(getOrCreateDirectMessage(userId)).unwrap();
      
      // Refresh direct messages list
      await dispatch(fetchDirectMessages());
      
      // Select the channel
      dispatch(setCurrentChannel(channel));
      
      dispatch(addNotification({
        type: 'success',
        title: 'Success',
        message: 'Direct message started',
      }));
      
      // Close the modal
      onClose();
      
      // Call the callback if provided (for custom handling)
      if (onStartConversation) {
        onStartConversation(channel);
      }
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: typeof error === 'string' ? error : 'Failed to start direct message',
      }));
    } finally {
      setStartingConversation(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] overflow-y-auto" onClick={onClose}>
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        <div
          className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full"
          onClick={(e) => e.stopPropagation()}
        >
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading profile...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <svg className="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>
              <button
                onClick={onClose}
                className="mt-4 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          ) : user ? (
            <>
              {/* Header */}
              <div className="bg-primary-600 px-6 py-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-white">User Profile</h3>
                  <button
                    onClick={onClose}
                    className="text-white hover:text-gray-200 focus:outline-none"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Tab Switcher */}
                <div className="flex p-1 bg-white/10 rounded-xl">
                  <button
                    onClick={() => setActiveTab('profile')}
                    className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-all ${
                      activeTab === 'profile'
                        ? 'bg-white text-primary-600 shadow-sm'
                        : 'text-white/80 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    Profile Info
                  </button>
                  <button
                    onClick={() => setActiveTab('files')}
                    className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-all ${
                      activeTab === 'files'
                        ? 'bg-white text-primary-600 shadow-sm'
                        : 'text-white/80 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    Shared Resources
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="max-h-[60vh] overflow-y-auto min-h-[300px]">
                {activeTab === 'profile' ? (
                  <div className="px-6 py-6">
                    {/* Profile Picture and Name */}
                    <div className="flex flex-col items-center mb-6">
                      <div className="relative">
                        <PresignedAvatar
                          fileKey={user.profilePicture}
                          name={`${user.firstName} ${user.lastName}`}
                          initials={user.firstName?.[0] || user.username?.[0] || 'U'}
                          className="w-24 h-24 rounded-full object-cover border-4 border-white dark:border-gray-800 shadow-lg"
                          fallbackClassName="w-24 h-24 bg-primary-500 rounded-full flex items-center justify-center border-4 border-white dark:border-gray-800 shadow-lg text-white text-3xl font-medium"
                        />
                        {/* Status Indicator */}
                        {user.status && (
                          <div
                            className={`absolute bottom-0 right-0 w-6 h-6 ${getStatusColor(user)} rounded-full border-4 border-white dark:border-gray-800`}
                            title={getStatusText(user)}
                          ></div>
                        )}
                      </div>
                      <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">
                        {user.firstName} {user.lastName}
                      </h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400">@{user.username}</p>
                      {user.status && (
                        <span className={`mt-2 px-3 py-1 text-xs font-medium rounded-full ${
                          resolveWorkLocation(user) === 'WORK_FROM_HOME'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
                            : resolveWorkLocation(user) === 'WORK_FROM_OFFICE'
                              ? 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200'
                              : ['ONLINE', 'AVAILABLE'].includes(user.status)
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : user.status === 'AWAY' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                          user.status === 'LONG_AWAY' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' :
                          user.status === 'LOGGED_OUT' ? 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200' :
                          user.status === 'ON_LEAVE' ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200' :
                          ['BUSY', 'DO_NOT_DISTURB'].includes(user.status) ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                          user.status === 'IN_A_CALL' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                          user.status === 'SICK_LEAVE' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                          user.status === 'HOLIDAY' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                          user.status === 'HAVING_FOOD' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' :
                          'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {getStatusText(user)}
                        </span>
                      )}
                    </div>

                    {/* Contact */}
                    <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-6">
                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Contact
                      </h4>
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-gray-500 dark:text-gray-400">Email</p>
                            {user.email ? (
                              <a
                                href={`mailto:${user.email}`}
                                className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline break-all"
                              >
                                {user.email}
                              </a>
                            ) : (
                              <p className="text-sm text-gray-400 dark:text-gray-500">Not provided</p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-gray-500 dark:text-gray-400">Date of birth</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {(() => {
                                const match = String(user.dateOfBirth || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
                                if (!match) return 'Not set';
                                return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
                                  .toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
                              })()}
                            </p>
                          </div>
                        </div>

                        {user.contactNumber?.trim() && (
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-green-50 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs text-gray-500 dark:text-gray-400">Phone</p>
                              <a
                                href={`tel:${normalizePhoneForTel(user.contactNumber)}`}
                                className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                              >
                                {user.contactNumber}
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {!isOwnProfile && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                        <MemberRelationshipEditor
                          memberName={`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username}
                          relationship={relationship}
                          loading={relationshipLoading}
                          saving={relationshipSaving}
                          onSave={handleSaveRelationship}
                          onDelete={handleDeleteRelationship}
                        />
                      </div>
                    )}

                    {/* User Details */}
                    <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
                      {user.role && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Role
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              user.role === 'ADMIN' 
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' 
                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            }`}>
                              {user.role}
                            </span>
                          </p>
                        </div>
                      )}

                      {isOwnProfile && user.lastSeen && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Last Seen
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(user.lastSeen)}</p>
                        </div>
                      )}

                      {!isOwnProfile && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Joined on
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">{formatJoinDate(user.createdAt)}</p>
                        </div>
                      )}

                      {/* Social Links Section */}
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Social Links
                        </label>
                        {hasAnySocialLink(user.socialLinks) ? (
                          <div className="mt-3 flex flex-wrap gap-2.5">
                            {buildSocialLinkItems(user.socialLinks).map((item) => (
                              <SocialIconLink
                                key={item.id}
                                href={item.href}
                                label={item.label}
                                platform={item.platform}
                              />
                            ))}
                          </div>
                        ) : (
                          <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">No social links added yet</p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="min-h-[300px]">
                    {dmChannelId ? (
                      <SharedFilesList channelId={dmChannelId} isDirectMessage={true} />
                    ) : (
                      <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                        <svg className="w-12 h-12 mb-4 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        <p className="text-sm">Start a conversation to see shared resources</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200 dark:border-gray-700">
                {userId !== currentUser?.id && (
                  <button
                    onClick={handleStartConversation}
                    disabled={startingConversation}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {startingConversation ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 12 5.373 12 12H4z"></path>
                        </svg>
                        <span>Starting...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        <span>Start Conversation</span>
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                >
                  Close
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UserProfileModal;
